import { useEffect, useRef } from 'react'
import { DocsVideo } from '@/components/DocsVideo'

export function VideoLightbox({ isOpen, onClose, src, poster, returnFocusRef }) {
  const closeBtnRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    closeBtnRef.current?.focus()

    return () => {
      document.body.style.overflow = prevOverflow
      returnFocusRef?.current?.focus()
    }
  }, [isOpen, returnFocusRef])

  useEffect(() => {
    if (!isOpen) return
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const root = containerRef.current
        if (!root) return
        const focusables = Array.from(
          root.querySelectorAll('button, [href]:not([tabindex="-1"])'),
        ).filter((el) => !el.hasAttribute('disabled'))
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        const active = document.activeElement
        if (e.shiftKey && (active === first || !root.contains(active))) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && (active === last || !root.contains(active))) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Recost intro video"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(10, 10, 10, 0.88)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'recostLightboxOverlay 200ms ease-out both',
      }}
    >
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: 'min(90vw, 1280px)',
          borderRadius: 12,
          overflow: 'hidden',
          background: '#000',
          boxShadow:
            '0 30px 80px -20px rgba(0, 0, 0, 0.65), 0 12px 32px rgba(0, 0, 0, 0.45)',
          animation:
            'recostLightboxScale 240ms cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        <DocsVideo src={src} poster={poster} autoStart ariaLabel="Recost intro video" />

        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          aria-label="Close video"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fafafa',
            background: 'rgba(10, 10, 10, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            borderRadius: 999,
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 10,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 2 L12 12 M12 2 L2 12"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes recostLightboxOverlay {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes recostLightboxScale {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  )
}
